import { CheckCircle2, Lightbulb, School, Dumbbell, FileText, MapPin, Building2, Coins, History, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, Eyebrow } from "./Reveal";

const feiten = [
  { icon: School, title: "Nieuwe school komt er", text: "De gemeente gaat de school in Menaam nieuw bouwen. Dit is het enige concrete besluit dat al genomen is." },
  { icon: Dumbbell, title: "Dorpsaccommodaties zijn verouderd", text: "De huidige gymzaal voldoet niet meer aan de eisen. Er moet iets nieuws komen voor bewegingsonderwijs." },
  { icon: FileText, title: "Gemeente heeft accommodatiebeleid", text: "Sinds juli 2025 heeft gemeente Waadhoeke een kader voor renovatie en nieuwbouw van accommodaties. Menaam valt hieronder." },
];

const open = [
  { icon: MapPin, title: "Locatie nieuwe school is nog niet bepaald", text: "Er is nog geen besluit genomen hoe de nieuwe school er uit gaat zien en welke locatie het meest geschikt is. Begin 2027 neemt de gemeenteraad een besluit." },
  { icon: Building2, title: "MFC / dorpshuis is een wens", text: "Een multifunctioneel centrum naast de school is een jarenlange wens van het dorp. Nu wordt er aan nieuwe plannen gewerkt om die wens werkelijkheid te maken." },
  { icon: Coins, title: "Hoe zit het met de financiering en exploitatie?", text: "Omdat het project zich nog in de planfase bevindt, worden de financiering en de exploitatie in een later stadium verder uitgewerkt. Eerst moeten de mogelijke plannen, locatie en de invulling voldoende concreet zijn. Daarna kan ook de financiële uitwerking worden afgerond." },
];

const Card = ({ item, dark }) => {
  const Icon = item.icon;
  return (
    <div
      data-testid={`situatie-card-${item.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      className={`flex gap-4 rounded-xl p-5 transition-transform hover:-translate-y-0.5 ${
        dark ? "bg-white/5 border border-white/10" : "bg-white border border-[#2D6A4F]/15"
      }`}
    >
      <div
        className={`shrink-0 w-11 h-11 rounded-lg flex items-center justify-center ${
          dark ? "bg-[#74C69D]/15 text-[#74C69D]" : "bg-[#F4A261]/15 text-[#b85f20]"
        }`}
      >
        <Icon size={20} />
      </div>
      <div>
        <h3 className={`font-heading text-lg font-bold mb-1 ${dark ? "text-[#FAF7F2]" : "text-[#1A2E22]"}`}>
          {item.title}
        </h3>
        <p className={`text-sm leading-relaxed m-0 ${dark ? "text-[#FAF7F2]/65" : "text-[#1A2E22]/65"}`}>
          {item.text}
        </p>
      </div>
    </div>
  );
};

export default function Situatie() {
  return (
    <section id="situatie" data-testid="situatie-section" className="py-24 sm:py-32 bg-[#FAF7F2]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <Eyebrow testid="situatie-eyebrow">Stand van zaken</Eyebrow>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1A2E22] mb-14 max-w-2xl">
            Wat staat vast, en wat is nog open?
          </h2>
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Fixed - dark green panel */}
          <Reveal>
            <div className="h-full rounded-3xl bg-[#2D6A4F] p-7 sm:p-9">
              <div className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.1em] uppercase px-4 py-2 rounded-full bg-white/10 text-[#74C69D] mb-6">
                <CheckCircle2 size={16} /> Dit staat vast
              </div>
              <div className="space-y-4">
                {feiten.map((f) => <Card key={f.title} item={f} dark />)}
              </div>
            </div>
          </Reveal>

          {/* Open - light panel */}
          <Reveal delay={0.1}>
            <div className="h-full rounded-3xl bg-white border border-[#2D6A4F]/15 p-7 sm:p-9">
              <div className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.1em] uppercase px-4 py-2 rounded-full bg-[#F4A261]/15 text-[#b85f20] mb-6">
                <Lightbulb size={16} /> Dit wordt nog onderzocht
              </div>
              <div className="space-y-4">
                {open.map((f) => <Card key={f.title} item={f} />)}
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <Link
            to="/historie"
            data-testid="situatie-historie-link"
            className="group mt-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-3xl bg-[#1A2E22] px-7 sm:px-9 py-7 transition-colors hover:bg-[#16271d]"
          >
            <div className="flex items-start gap-4">
              <div className="shrink-0 w-12 h-12 rounded-xl bg-[#74C69D]/15 text-[#74C69D] flex items-center justify-center">
                <History size={22} />
              </div>
              <div>
                <h3 className="font-heading text-xl font-bold text-[#FAF7F2] mb-1">Hoe we hier gekomen zijn</h3>
                <p className="text-sm text-[#FAF7F2]/65 m-0 max-w-xl">
                  Lees de historische context: eerdere pogingen, het verlies van het zwembad en de lessen die we meenemen.
                </p>
              </div>
            </div>
            <span className="inline-flex items-center gap-2 font-bold text-sm px-6 py-3 rounded-full bg-[#74C69D] text-[#1A2E22] shrink-0 self-start sm:self-center">
              Bekijk de historie
              <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
            </span>
          </Link>
        </Reveal>
      </div>
    </section>
  );
}
